<template>
  <div id="instance">
    <Intervention v-if="metricsTimeout" class="intervention-msg" />
    <Task :instance="instance" />

    <el-row class="header">
      <el-col :span="14">
        <h2>
          <el-button type="text" @click="$router.push('/instances/' + instance.id)">
            <img :src="regionFlag(instance.planId)" />
          </el-button>

          <EditName
            :name="instance.name"
            size="mini"
            @change="updateName"
          />

          <span v-if="plan" class="plan">
            {{ planDescription(plan.id) }} /
            {{ instance.storage.size }} GB Storage /
            {{ plan.region.name }} / {{ plan.name }}
          </span>
        </h2>
      </el-col>

      <el-col :span="10">
        <Metrics
          class="metrics"
          :instance="instance"
          :timeout.sync="metricsTimeout"
          summary
        />
      </el-col>
    </el-row>

    <el-row class="actions" :gutter="15">
      <el-col class="metadata" :span="16">
        <div
          v-if="$route.name === 'application'"
          class="el-page-header__left"
          @click.prevent="$router.push('/instances/' + instance.id)"
        >
          <i class="el-icon-back" />
          <div class="el-page-header__title">Back</div>
        </div>

        <span v-if="instance.publicIp" class="ip">
          <strong>Public IP :</strong>
          <Clipboard id="public-ip" :value="instance.publicIp" />
        </span>

        <strong>Created :</strong> {{ toCalendar(instance.created) }}

        <span v-if="instance.started" class="uptime">
          <strong>Uptime :</strong> {{ fromNow(instance.started) }}
        </span>
      </el-col>

      <el-col :span="8">
        <State
          v-if="instance.state !== 'running'"
          :instance="instance"
        />

        <Actions
          v-else
          :plan="plan"
          :instance="instance"
        />
      </el-col>
    </el-row>

    <Domain
      :domains="instanceDomains"
      :instance="instance"
      :instance-applications="instanceApplications"
    />

    <el-container v-loading="instance.state !== 'running'">
      <el-main
        class="router-view"
        v-bind:class="{
          applications: $route.name === 'layout',
          application: $route.name === 'application'
        }"
      >
        <router-view
          v-if="$route.name !== 'layout'"
          :plan="plan"
          :instance="instance"
          :application="application"
          :domains="applicationDomains"
          @back="$router.push('/instances/' + instance.id)"
        />

        <template v-else-if="instanceApplications.length > 0 || instanceRepositories.length > 0">
          <el-row :gutter="15">
            <el-col
              v-for="application in instanceApplications"
              :key="application.id"
              :span="8"
            >
              <ApplicationCard :application="application" />
            </el-col>
          </el-row>

          <el-divider
            v-if="instanceRepositories.length > 0"
            content-position="left"
          >
            Repositories
          </el-divider>

          <BuildCard
            v-for="repository in instanceRepositories"
            :key="repository.id"
            :repository="repository"
          />
        </template>

        <div v-else class="align-center">
          <img src="../../assets/hero/choose-application.svg" class="nothing" />
          <p>Start a first application by choosing a micro-service on right side</p>
        </div>
      </el-main>

      <el-aside class="gradient" width="260px">
        <template v-if="application">
          <h4 class="name">
            <EditName
              :name="application.alias"
              size="mini"
              @change="updateAlias"
            />
          </h4>

          <ApplicationInstruction
            class="application-instruction"
            :bundle-id="application.bundleId"
            collapse
          />

          <ApplicationLinks
            class="application-links"
            :application="application"
            :instance-id="instance.id"
          />
        </template>

        <template v-else>
          <el-button
            v-for="(item, key) in bundles"
            v-bind:class="{ [key]: true, disabled: item.disabled, app: true }"
            v-text="item.name"
            :key="key"
            type="text"
            @click="deploy(item, key)"
          />
          <el-button
            class="git app"
            type="text"
            :disabled="true"
            @click="createGit = true"
          >
            Git repository
          </el-button>

          <BuildCreate :instance="instance" :open.sync="createGit" />
        </template>
      </el-aside>
    </el-container>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapState } from 'vuex'
import State from './State'
import Metrics from './Metrics'
import Actions from './Actions'
import Intervention from './Intervention'
import Task from './Task'
import Domain from './Domain'
import Clipboard from '../Clipboard'
import EditName from '../EditName'
import ApplicationCard from '../Application/Card'
import ApplicationLinks from '../Application/Links/List'
import ApplicationInstruction from '../Application/Instruction'
import BuildCard from '../Build/Card'
import BuildCreate from '../Build/Create'

export default {
  props: {
    instance: Object
  },
  components: {
    State,
    Metrics,
    Actions,
    Intervention,
    Task,
    Domain,
    Clipboard,
    EditName,
    ApplicationCard,
    ApplicationLinks,
    ApplicationInstruction,
    BuildCard,
    BuildCreate
  },
  data () {
    return {
      metricsTimeout: null,
      createGit: false
    }
  },
  computed: {
    plan () {
      return this.plans.find((p) => p.id === this.instance.planId)
    },
    application () {
      return this.instanceApplications.find((a) => a.id === this.$route.params.applicationId)
    },
    applicationDomains () {
      return this.domains.filter((d) => this.application && d.applications.find((a) => a.id === this.application.id))
    },
    instanceApplications () {
      return this.applications.filter((a) => a.instanceId === this.instance.id)
    },
    instanceRepositories () {
      return this.repositories.filter((r) => r.instanceId === this.instance.id)
    },
    instanceDomains () {
      const applications = this.instanceApplications.map((a) => a.id)
      return this.domains.filter((d) => d.applications.find((a) => applications.indexOf(a.id) !== -1))
    },
    bundles () {
      return _.omitBy(this.$root.bundles, (b) => b.database)
    },
    ...mapState(['applications', 'repositories', 'domains'])
  },
  methods: {
    deploy (bundle, bundleId) {
      if (!bundle.disabled) {
        this.$router.push('/instances/' + this.instance.id + '/bundle/' + bundleId)
      }
    },
    updateName (name) {
      this.$store.dispatch('updateInstance', {
        name,
        id: this.instance.id
      }).catch(() => {})
    },
    updateAlias (alias) {
      this.$store.dispatch('updateApplication', {
        alias,
        id: this.application.id,
        instanceId: this.application.instanceId
      }).catch(() => {})
    }
  }
}
</script>

<style lang="scss">
  @import "../../theme";

  #instance {
    .el-divider__text {
      font-weight: 400;
      background-color: $--background-color-base;
    }
    .header {
      .el-input {
        width: calc(100% - 100px);
      }
    }
    .actions {
      .el-progress {
        margin-top: 9px;
      }
      .el-page-header__left {
        float: left;

        ::after {
          background-color: $--color-text-gray;
        }
      }
    }
    .el-page-header {
      border-bottom: 1px solid $--border-color-base;
      background-color: $--background-color-base;
      padding: 12px 0 12px 48px;
      margin: -15px -15px 20px -48px;

      .el-page-header__content {
        font-size: 16px;
      }
    }
    .application {
      padding-left: 12px;
    }
    .application-instruction {
      .el-collapse-item__header {
        font-weight: 400;
      }
      .el-collapse-item {
        padding-left: 25px;
      }
    }
  }
</style>

<style lang="scss" scoped>
  @import "../../theme";

  #instance {
    .intervention-msg {
      margin: 0;
      padding-left: 48px;
      border-top: none;
    }

    .header {
      margin: 0;
      padding: 12px 48px;
      border-bottom: 1px solid $--border-color-base;
      background-color: #fff;

      .metrics {
        text-align: right;
        font-weight: 400;
        font-size: 13px;
        line-height: 18px;
        padding-top: 8px;
      }
    }

    h2 {
      line-height: 22px;
      font-size: 18px;
      margin: 0;
      padding-top: 0;

      .el-button {
        float: left;
        margin: 0 10px 0 0;
        padding: 0;

        img {
          max-width: 64px;
        }
      }

      .plan {
        display: block;
        font-weight: 400;
        font-size: 12px;
        color: $--color-text-gray;
      }
    }

    .actions {
      border-bottom: 1px solid $--border-color-base;
      text-align: right;
      margin: 0 !important;
      padding: 6px 48px;
      line-height: 28px;
      font-size: 13px;

      .metadata {
        text-align: left;
        opacity: 0.8;

        .ip {
          margin-right: 15px;
        }
        .uptime {
          margin-left: 15px;
        }
      }

      .el-col {
        padding: 0 !important;
      }
    }

    .el-container {
      height: calc(100% - 115px);
    }
    .el-main {
      padding: 15px 15px 15px 48px;
      background: #ffffff;

      &.router-view {
        overflow: visible;
        padding-bottom: 50px;
      }
      &.applications {
        padding-left: 48px;
        background-color: $--background-color-gradient;

        .application {
          margin-bottom: 15px;
          padding-left: 0;
        }
      }
      &.application {
        padding: 0;
      }

      .nothing {
        max-height: 400px;
        padding-top: 30px;
      }
    }
    .el-aside {
      padding: 20px;
      border-left: 1px solid $--border-color-base;
      overflow-x: hidden !important;

      .name {
        margin: -10px 0 10px;
        max-width: 230px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .el-divider {
        margin: 30px 0;
      }

      .app {
        display: block;
        padding: 0 0 0 50px;
        margin: 0 0 25px 0;
        font-weight: 500;
        font-size: 14px;
        line-height: 35px;
        background-repeat: no-repeat;
        background-size: 30px;
        background-position: left;
        color: $--color-text-primary;
      }

      .application-instruction {
        margin: 0 -25px 10px -25px;
        background-color: #fff;
      }

      .application-links {
      }
    }
  }
</style>
